/**
 * Two-Factor Authentication Verification Page
 *
 * Displayed after OAuth login when user has 2FA enabled.
 * Accepts TOTP code or backup code.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import authService, { postLoginRedirect } from '../../services/authService.js';

function TwoFactorVerify() {
    const navigate = useNavigate();
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [useBackupCode, setUseBackupCode] = useState(false);
    const [backupCode, setBackupCode] = useState('');
    const [backupWarning, setBackupWarning] = useState(null);
    const inputRefs = useRef([]);

    // Check if there's a pending 2FA verification
    useEffect(() => {
        if (!authService.hasPending2FA()) {
            navigate('/login', { replace: true });
        }
    }, [navigate]);

    // Handle individual digit input
    const handleCodeChange = (index, value) => {
        // Only allow digits
        if (value && !/^\d$/.test(value)) return;

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);
        setError(null);

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        // Auto-submit when all digits entered
        if (value && index === 5 && newCode.every(d => d !== '')) {
            handleVerify(newCode.join(''));
        }
    };

    // Handle paste
    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pasted.length === 6) {
            const newCode = pasted.split('');
            setCode(newCode);
            inputRefs.current[5]?.focus();
            handleVerify(pasted);
        }
    };

    // Handle backspace
    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    // Verify TOTP code
    const handleVerify = async (codeString) => {
        if (loading) return;

        const verifyCode = codeString || code.join('');
        if (verifyCode.length !== 6) {
            setError('Please enter all 6 digits');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await authService.verify2FA(verifyCode);

            // Success - redirect to the completed session's destination. A GTA session
            // (any axis) goes to /gta via the shared resolver; never a platform portal.
            navigate(postLoginRedirect(authService.getSession()), { replace: true });
        } catch (err) {
            setError(err.message || 'Invalid verification code');
            setCode(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    // Verify backup code
    const handleBackupVerify = async (e) => {
        e.preventDefault();
        if (loading) return;

        const cleanCode = backupCode.replace(/\s/g, '').toUpperCase();
        if (!cleanCode) {
            setError('Please enter a backup code');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const result = await authService.verify2FABackup(cleanCode);

            if (result.warning) {
                setBackupWarning(result.warning);
            }

            // Success - redirect to the completed session's destination (GTA → /gta).
            const dest = postLoginRedirect(authService.getSession());
            setTimeout(() => {
                navigate(dest, { replace: true });
            }, result.warning ? 2000 : 0);
        } catch (err) {
            setError(err.message || 'Invalid backup code');
        } finally {
            setLoading(false);
        }
    };

    // Cancel and return to login
    const handleCancel = () => {
        authService.cancelPending2FA();
        navigate('/login', { replace: true });
    };

    return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
            <div className="max-w-md w-full">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 mb-2">Two-Factor Authentication</h1>
                    <p className="text-slate-600">
                        {useBackupCode
                            ? 'Enter one of your backup codes'
                            : 'Enter the 6-digit code from your authenticator app'}
                    </p>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}

                    {backupWarning && (
                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm text-yellow-700">{backupWarning}</p>
                        </div>
                    )}

                    {!useBackupCode ? (
                        // TOTP Code Entry
                        <div>
                            <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
                                {code.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => (inputRefs.current[index] = el)}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleCodeChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        className="w-12 h-14 text-center text-2xl font-mono border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        disabled={loading}
                                        autoFocus={index === 0}
                                    />
                                ))}
                            </div>

                            <button
                                onClick={() => handleVerify()}
                                disabled={loading || code.some(d => d === '')}
                                className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {loading ? 'Verifying...' : 'Verify'}
                            </button>

                            <div className="mt-4 text-center">
                                <button
                                    onClick={() => setUseBackupCode(true)}
                                    className="text-sm text-blue-600 hover:text-blue-700"
                                >
                                    Use a backup code instead
                                </button>
                            </div>
                        </div>
                    ) : (
                        // Backup Code Entry
                        <form onSubmit={handleBackupVerify}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Backup Code
                                </label>
                                <input
                                    type="text"
                                    value={backupCode}
                                    onChange={(e) => { setBackupCode(e.target.value); setError(null); }}
                                    placeholder="XXXX-XXXX"
                                    className="w-full px-4 py-3 text-center text-lg font-mono border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                                    disabled={loading}
                                    autoFocus
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading || !backupCode}
                                className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {loading ? 'Verifying...' : 'Verify Backup Code'}
                            </button>

                            <div className="mt-4 text-center">
                                <button
                                    type="button"
                                    onClick={() => { setUseBackupCode(false); setBackupCode(''); setError(null); }}
                                    className="text-sm text-blue-600 hover:text-blue-700"
                                >
                                    Use authenticator app instead
                                </button>
                            </div>
                        </form>
                    )}

                    <div className="mt-6 pt-4 border-t border-slate-200 text-center">
                        <button
                            onClick={handleCancel}
                            className="text-sm text-slate-600 hover:text-slate-900"
                        >
                            Cancel and return to login
                        </button>
                    </div>
                </div>

                <p className="mt-6 text-center text-xs text-slate-500">
                    Having trouble? Contact support@agenticed.com
                </p>
            </div>
        </div>
    );
}

export default TwoFactorVerify;
